import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Context,
  type ReactNode,
} from "react";
import type { ButtonVariant } from "@/shared/ui/core";
import { useMediaQuery } from "@/shared/lib/useMediaQuery";

export type PageAction = {
  id: string;
  label: string;
  to?: string;
  onClick?: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  menuOnly?: boolean;
  /** Destructive row in the overflow menu — red label, same as list actions. */
  danger?: boolean;
  title?: string;
  /** Lucide key rendered by toolbar (serializable). */
  iconKey?: string;
  /** Marks this item as a quick navigation link / jump to another entity or screen. */
  isQuickLink?: boolean;
};

export type PageFilterToggle = {
  open: boolean;
  onToggle: () => void;
  activeCount?: number;
};

/** Same shape as filter — show/hide ModuleInsights KPI strip. */
export type PageInsightsToggle = {
  open: boolean;
  onToggle: () => void;
  /** Optional alert badge (e.g. overdue count). */
  alertCount?: number;
  /** Optional KPI or item count (renders primary badge). */
  count?: number;
  badgeTone?: "danger" | "primary";
};

/** Extra breadcrumb segments after the list screen (entity code, nested pages). */
export type PageBreadcrumbTrailItem = {
  label: string;
  to?: string | null;
};

type PageActionsContextValue = {
  actions: PageAction[];
  setActions: (actions: PageAction[]) => void;
  filterToggle: PageFilterToggle | null;
  setFilterToggle: (toggle: PageFilterToggle | null) => void;
  insightsToggle: PageInsightsToggle | null;
  setInsightsToggle: (toggle: PageInsightsToggle | null) => void;
  breadcrumbTrail: PageBreadcrumbTrailItem[];
  setBreadcrumbTrail: (items: PageBreadcrumbTrailItem[]) => void;
};

const CONTEXT_KEY = "__galaxy_page_actions_context__";

/** Stable Context across Vite HMR (same pattern as PrintPreviewContext). */
function getPageActionsContext(): Context<PageActionsContextValue | null> {
  const g = globalThis as typeof globalThis & {
    [CONTEXT_KEY]?: Context<PageActionsContextValue | null>;
  };
  if (!g[CONTEXT_KEY]) {
    g[CONTEXT_KEY] = createContext<PageActionsContextValue | null>(null);
  }
  return g[CONTEXT_KEY];
}

const PageActionsContext = getPageActionsContext();

export function PageActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActionsState] = useState<PageAction[]>([]);
  const [filterToggle, setFilterToggleState] = useState<PageFilterToggle | null>(null);
  const [insightsToggle, setInsightsToggleState] = useState<PageInsightsToggle | null>(
    null,
  );
  const [breadcrumbTrail, setBreadcrumbTrailState] = useState<PageBreadcrumbTrailItem[]>([]);

  const setActions = useCallback((next: PageAction[]) => {
    setActionsState((prev) => {
      if (prev === next) return prev;
      if (prev.length === next.length) {
        const same = prev.every(
          (p, i) =>
            p.id === next[i]?.id &&
            p.label === next[i]?.label &&
            p.disabled === next[i]?.disabled &&
            p.variant === next[i]?.variant &&
            p.to === next[i]?.to &&
            p.danger === next[i]?.danger &&
            p.iconKey === next[i]?.iconKey &&
            p.isQuickLink === next[i]?.isQuickLink &&
            p.onClick === next[i]?.onClick,
        );
        if (same) return prev;
      }
      return next;
    });
  }, []);

  const setFilterToggle = useCallback((next: PageFilterToggle | null) => {
    setFilterToggleState((prev) => {
      if (!prev && !next) return prev;
      if (
        prev &&
        next &&
        prev.open === next.open &&
        prev.activeCount === next.activeCount &&
        prev.onToggle === next.onToggle
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const setInsightsToggle = useCallback((next: PageInsightsToggle | null) => {
    setInsightsToggleState((prev) => {
      if (!prev && !next) return prev;
      if (
        prev &&
        next &&
        prev.open === next.open &&
        prev.alertCount === next.alertCount &&
        prev.count === next.count &&
        prev.badgeTone === next.badgeTone &&
        prev.onToggle === next.onToggle
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const setBreadcrumbTrail = useCallback((next: PageBreadcrumbTrailItem[]) => {
    setBreadcrumbTrailState((prev) => {
      if (prev === next) return prev;
      if (prev.length === next.length) {
        const same = prev.every(
          (p, i) => p.label === next[i]?.label && p.to === next[i]?.to,
        );
        if (same) return prev;
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      actions,
      setActions,
      filterToggle,
      setFilterToggle,
      insightsToggle,
      setInsightsToggle,
      breadcrumbTrail,
      setBreadcrumbTrail,
    }),
    [
      actions,
      setActions,
      filterToggle,
      setFilterToggle,
      insightsToggle,
      setInsightsToggle,
      breadcrumbTrail,
      setBreadcrumbTrail,
    ],
  );

  return (
    <PageActionsContext.Provider value={value}>{children}</PageActionsContext.Provider>
  );
}

function usePageActionsContext(): PageActionsContextValue {
  const ctx = useContext(PageActionsContext);
  if (!ctx) {
    throw new Error("usePageActions must be used within PageActionsProvider");
  }
  return ctx;
}

export function usePageActionsState(): PageAction[] {
  return usePageActionsContext().actions;
}

export function usePageFilterToggleState(): PageFilterToggle | null {
  return usePageActionsContext().filterToggle;
}

export function usePageInsightsToggleState(): PageInsightsToggle | null {
  return usePageActionsContext().insightsToggle;
}

export function usePageBreadcrumbTrailState(): PageBreadcrumbTrailItem[] {
  return usePageActionsContext().breadcrumbTrail;
}

/** Automatically clear filterToggle when navigation happens so stale filters never leak across pages. */
export function useResetFilterToggleOnPathChange(path: string) {
  const { setFilterToggle } = usePageActionsContext();
  const prevPathRef = useRef(path);

  useEffect(() => {
    if (prevPathRef.current !== path) {
      prevPathRef.current = path;
      setFilterToggle(null);
    }
  }, [path, setFilterToggle]);
}

/**
 * Append segments after the Ops list breadcrumb (e.g. entity code).
 * Empty array clears override so the shell may infer from the URL.
 */
export function usePageBreadcrumbTrail(items: PageBreadcrumbTrailItem[]) {
  const { setBreadcrumbTrail } = usePageActionsContext();
  const serialized = JSON.stringify(items);

  useEffect(() => {
    setBreadcrumbTrail(items);
    return () => setBreadcrumbTrail([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- serialized identity
  }, [serialized, setBreadcrumbTrail]);
}

/** Register page-level actions for the Ops breadcrumb toolbar row. */
export function usePageActions(actions: PageAction[]) {
  const { setActions } = usePageActionsContext();
  const serialized = JSON.stringify(
    actions.map((a) => ({
      id: a.id,
      label: a.label,
      to: a.to,
      variant: a.variant,
      disabled: a.disabled,
      menuOnly: a.menuOnly,
      danger: a.danger,
      title: a.title,
      iconKey: a.iconKey,
      isQuickLink: a.isQuickLink,
    })),
  );

  useEffect(() => {
    setActions(actions);
    return () => setActions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- serialized identity
  }, [serialized, setActions]);
}

/**
 * Registers filter show/hide on the breadcrumb row.
 * Returns whether the filter panel should be visible.
 * Always open by default on all screens unless explicitly configured with initialOpen: false.
 */
export function usePageFilterPanel(options?: {
  initialOpen?: boolean;
  activeCount?: number;
  enabled?: boolean;
  open?: boolean;
  onToggle?: () => void;
}): boolean {
  const { setFilterToggle } = usePageActionsContext();
  const [internalOpen, setInternalOpen] = useState(options?.initialOpen ?? true);
  const open = options?.open !== undefined ? options.open : internalOpen;
  const activeCount = options?.activeCount ?? 0;
  const enabled = options?.enabled ?? true;

  const onToggle = options?.onToggle;
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;

  const stableToggle = useCallback(() => {
    if (onToggleRef.current) {
      onToggleRef.current();
    } else {
      setInternalOpen((v) => !v);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setFilterToggle(null);
      return;
    }
    setFilterToggle({
      open,
      onToggle: stableToggle,
      activeCount,
    });
  }, [open, stableToggle, activeCount, enabled, setFilterToggle]);

  useEffect(() => () => setFilterToggle(null), [setFilterToggle]);

  return enabled && open;
}

/**
 * Registers ModuleInsights / KPI strip show/hide on the breadcrumb row.
 * Always open by default on all screens (desktop & mobile) unless explicitly disabled with initialOpen: false.
 */
export function usePageInsightsPanel(options?: {
  initialOpen?: boolean;
  alertCount?: number;
  count?: number;
  badgeTone?: "danger" | "primary";
  enabled?: boolean;
}): boolean {
  const { setInsightsToggle } = usePageActionsContext();
  const preferred = options?.initialOpen !== undefined ? options.initialOpen : true;
  const userTouched = useRef(false);
  const [open, setOpen] = useState(preferred);
  const alertCount = options?.alertCount ?? 0;
  const count = options?.count;
  const badgeTone = options?.badgeTone;
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    if (userTouched.current) return;
    setOpen(preferred);
  }, [preferred]);

  const stableToggle = useCallback(() => {
    userTouched.current = true;
    setOpen((v) => !v);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setInsightsToggle(null);
      return;
    }
    setInsightsToggle({
      open,
      onToggle: stableToggle,
      alertCount,
      count,
      badgeTone,
    });
  }, [open, stableToggle, alertCount, count, badgeTone, enabled, setInsightsToggle]);

  useEffect(() => () => setInsightsToggle(null), [setInsightsToggle]);

  return enabled && open;
}

export const PAGE_ACTIONS_INLINE_MAX = 2;

export function splitPageActions(actions: PageAction[]): {
  inline: PageAction[];
  menu: PageAction[];
} {
  const forcedMenu = actions.filter((a) => a.menuOnly);
  const candidates = actions.filter((a) => !a.menuOnly);
  /** Prefer primary/danger for the 1–2 inline slots (MX3 phone + desktop D-DT4). */
  const ranked = [...candidates].sort((a, b) => {
    const rank = (x: PageAction) =>
      x.variant === "primary" ? 0 : x.variant === "danger" ? 1 : 2;
    return rank(a) - rank(b);
  });
  const inline = ranked.slice(0, PAGE_ACTIONS_INLINE_MAX);
  const overflow = ranked.slice(PAGE_ACTIONS_INLINE_MAX);
  return { inline, menu: [...overflow, ...forcedMenu] };
}
